import { createFileRoute } from '@tanstack/react-router'

const StoragePage = () => {
  return (
    <div className="mt-6 flex-col pb-6 sm:w-full">
      <h4 className="mb-2 text-xl font-bold text-gray-800">
        Seed Storage Behaviour
      </h4>

      <p className="mb-2">
        In essence, seed storage behaviour refers to the capacity of seeds to
        survive desiccation. The classification used here largely follows that
        used in the hard copy{' '}
        <i>Compendium of Information on Seed Storage Behaviour</i> by T.D. Hong,
        S.H. Linington and R.H. Ellis, published in a limited run in 1998 by RBG
        Kew — ISBN&nbsp;1&nbsp;900347&nbsp;49&nbsp;0. A summary of the various
        storage categories used in SID is given below. More detailed information
        on the various storage categories, including the pitfalls and causes of
        misclassification, is available within the above reference.
      </p>
      <p className="mb-2">
        <strong>Orthodox</strong> seeds can be dried, without damage, to low
        moisture contents, usually much lower than those they would normally
        achieve in nature. Over a wide range of storage environments their
        longevity increases with reductions in both moisture content and
        temperature, in a quantifiable and predictable way.
      </p>

      <p className="mb-2">
        <strong>Recalcitrant</strong> seeds do not survive drying to any large
        degree, and are thus not amenable to long term storage, although the
        critical moisture level for survival varies among species. In this
        database this category includes those seeds, of some aquatic species in
        particular, described as viviparous.
      </p>

      <p className="mb-2">
        <strong>Intermediate</strong> seeds are more tolerant of desiccation
        than recalcitrants, though that tolerance is much more limited than is
        the case with orthodox seeds, and they generally lose viability more
        rapidly at low temperature. They do not conform to all the criteria
        defining orthodox seeds, especially in respect of the quantification and
        predictability of the relations between longevity and both drying and
        cooling.
      </p>

      <p className="mb-2">
        Where SID's compilers have judged from the evidence available that a
        species is likely (greater than evens chance), rather than certain, to
        display a particular storage behaviour, this is indicated by the
        addition of a question mark (e.g. 'recalcitrant ?'); and for the
        orthodox category only there is an additional subdivision of 'probable'
        ('orthodox p' — better than likely, but not absolutely certain). The
        database can also be searched using these sub-categories, or by
        combining them (e.g. 'any intermediate') — see the pick list for storage
        behaviour.
      </p>

      <p className="mb-2">
        A large part of the storage behaviour dataset that is presently included
        within SID (approximately 7000 of the 9582 species entries), originates
        from the electronic version of the{' '}
        <i>Compendium of Information on Seed Storage Behaviour.</i> This dataset
        was the result of a collaboration between the{' '}
        <a href="http://www.bioversityinternational.org/">
          International Plant Genetic Resources Institute
        </a>{' '}
        (IPGRI), the{' '}
        <a href="http://www.agric.rdg.ac.uk/">
          University of Reading Department of Agriculture
        </a>{' '}
        and RBG Kew, and was itself derived from an earlier hard copy version
        with very limited distribution, funded by IPGRI and compiled largely
        through Tran Dan Hong's herculean efforts. An electronic version of the{' '}
        <i>Compendium </i>database is also{' '}
        <a href="http://www.bioversityinternational.org/Themes/Genebanks/Species_Compendium/HBforGenebanks4introductiontossb.pdf">
          available for download
        </a>
        .
      </p>
    </div>
  )
}

export const Route = createFileRoute('/definitions/storage')({
  component: StoragePage,
})
